import { type ReactElement, useRef, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Link,
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { useAuth } from '~/app/providers';
import {
  useActiveWorkspace,
  useWorkspaceLocale,
  useWorkspaceWallet,
} from '~/stores/workspace';
import { BrandLogo } from '~/components/ui/BrandLogo';
import { AppImage } from '~/components/ui/AppImage';
import { Icon } from '~/components/ui/Icon';
import { AccountSettingsPopover } from './AccountSettingsPopover';
import { WorkspaceSwitcherModal } from '~/features/company/WorkspaceSwitcherModal';
import { AgentSessionHeader } from '~/features/studio/AgentSessionHeader';
import { AgentSessionHeaderProvider } from '~/features/studio/agentSessionHeaderContext';
import { ACTIVE_REGION_NAME_I18N_KEY } from '~/app/regionLabel';
import { studioNavigation } from '~/features/studio/studioData';
import { cn } from '~/utils';
import { ImageWatermarkProvider } from '~/features/assets/ImageWatermarkProvider';
import { BillingUpgradeProvider } from '~/features/billing/BillingUpgradeProvider';
import { AgentChatHistory } from './AgentChatHistory';
import { CreditLabel } from './CreditLabel';

const navPath = (slug: string): string =>
  slug === 'agent' ? '/agent?mode=video' : slug ? `/${slug}` : '/';

export const appShellOutletKey = (pathname: string): string => {
  if (pathname.startsWith('/agent')) return '/agent';
  if (pathname.startsWith('/video-editor/')) return '/video-editor';
  return pathname;
};

export const AppShell = (): ReactElement => {
  const { t } = useTranslation('navigation');
  const { t: tc } = useTranslation('common');
  const { t: ta } = useTranslation('account');
  const { t: ts } = useTranslation('studio');
  const { session } = useAuth();
  const activeWorkspace = useActiveWorkspace();
  const locale = useWorkspaceLocale();
  const authenticated = session !== null;
  const activeWorkspaceId = activeWorkspace?.id;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [chatHistoryOpen, setChatHistoryOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [workspaceSwitcherOpen, setWorkspaceSwitcherOpen] = useState(false);
  const { balance: walletBalance } = useWorkspaceWallet(
    authenticated ? activeWorkspaceId : null
  );
  const accountTriggerRef = useRef<HTMLButtonElement>(null);
  const workspaceSwitcherTriggerRef = useRef<HTMLButtonElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const isAgentRoute = location.pathname.startsWith('/agent');
  const isVideoEditorRoute = location.pathname.startsWith('/video-editor/');
  const isWorkspaceRoute = isAgentRoute || isVideoEditorRoute;
  const outletKey = appShellOutletKey(location.pathname);
  const showMobileBack = location.pathname !== '/';
  const activeRegionName = ta(ACTIVE_REGION_NAME_I18N_KEY);
  const activeRegionLabel = ta('regionLabel', { region: activeRegionName });
  const initials = session?.user.displayName
    .split(/\s+/)
    .map(part => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const sidebarLinkClass = ({ isActive }: { isActive: boolean }): string =>
    cn(
      'flex min-h-10 w-full items-center gap-3 rounded-xl px-3 text-sm font-medium transition focus-visible:outline-2 focus-visible:outline-primary',
      isActive
        ? 'bg-nav-active text-nav-active-text'
        : 'text-muted hover:bg-hover hover:text-foreground'
    );

  const renderSidebarNavItem = (
    item: (typeof studioNavigation)[number]
  ): ReactElement => (
    <NavLink
      className={sidebarLinkClass}
      end={item.slug === ''}
      key={item.slug || 'home'}
      reloadDocument={item.reloadDocument}
      to={navPath(item.slug)}
    >
      <Icon className="size-5 shrink-0" name={item.icon} />
      <span>{ts(item.translationKey)}</span>
    </NavLink>
  );

  return (
    <BillingUpgradeProvider key={activeWorkspaceId ?? 'no-workspace'}>
      <ImageWatermarkProvider>
        <AgentSessionHeaderProvider>
          <div className="app-shell flex h-dvh max-h-dvh w-full overflow-hidden bg-surface text-foreground">
            <a
              className="fixed left-3 top-3 z-[70] -translate-y-20 rounded-lg bg-pill px-3 py-2 text-sm font-semibold text-pill-text focus:translate-y-0"
              href="#main-content"
            >
              {tc('skip')}
            </a>

            <div className="flex h-full min-h-0 w-full min-w-0 flex-1 overflow-hidden">
              <aside
                className={cn(
                  'relative hidden shrink-0 border-r border-border-subtle bg-surface transition-[width] duration-200 lg:flex',
                  chatHistoryOpen ? 'w-[612px]' : 'w-[252px]'
                )}
              >
                <div className="flex min-h-0 w-[252px] shrink-0 flex-col overflow-hidden px-3 py-4">
                  <Link
                    className="mb-6 rounded-lg px-2 focus-visible:outline-2 focus-visible:outline-primary"
                    to="/"
                  >
                    <BrandLogo />
                  </Link>

                  <nav
                    aria-label={ts('navigation.primary')}
                    className="mb-4 grid content-start gap-1"
                  >
                    {studioNavigation.map(item => renderSidebarNavItem(item))}
                  </nav>

                  {isAgentRoute && authenticated && activeWorkspaceId ? (
                    <AgentChatHistory
                      onDrawerOpenChange={setChatHistoryOpen}
                      workspaceId={activeWorkspaceId}
                    />
                  ) : null}
                </div>
              </aside>

              <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
                <header className="z-40 shrink-0 border-b border-border-subtle bg-surface/90 pt-[env(safe-area-inset-top)] backdrop-blur-xl">
                  <div className="flex h-14 min-w-0 items-center gap-2 px-3 sm:gap-3 sm:px-6">
                    <button
                      aria-label={t('menu')}
                      className="grid size-10 place-items-center rounded-xl text-muted hover:bg-hover focus-visible:outline-2 focus-visible:outline-primary lg:hidden"
                      onClick={() => setMobileOpen(true)}
                      type="button"
                    >
                      <Icon name="menu" />
                    </button>

                    {showMobileBack ? (
                      <button
                        aria-label={tc('back')}
                        className="grid size-10 shrink-0 place-items-center rounded-xl text-muted hover:bg-hover focus-visible:outline-2 focus-visible:outline-primary lg:hidden"
                        onClick={() => {
                          const historyIndex = Number(
                            window.history.state?.idx ?? 0
                          );
                          if (historyIndex > 0) navigate(-1);
                          else navigate('/');
                        }}
                        type="button"
                      >
                        <ArrowLeft aria-hidden="true" className="size-5" />
                      </button>
                    ) : null}

                    <div className="hidden min-w-0 flex-1 lg:block">
                      {isAgentRoute ? <AgentSessionHeader /> : null}
                    </div>

                    {isAgentRoute ? (
                      <div className="min-w-0 flex-1 lg:hidden">
                        <AgentSessionHeader />
                      </div>
                    ) : null}

                    <div className="ml-auto flex shrink-0 items-center gap-1.5 sm:gap-2">
                      {session ? (
                        <>
                          <span
                            aria-label={activeRegionLabel}
                            className="hidden min-h-9 items-center rounded-lg border border-primary/25 bg-primary/10 px-2.5 font-mono text-[11px] font-semibold tracking-wide text-primary sm:inline-flex"
                            translate="no"
                          >
                            {activeRegionName}
                          </span>
                          <CreditLabel
                            balance={walletBalance}
                            hideUnitOnMobile
                            locale={locale}
                          />
                          <div className="relative">
                            <button
                              aria-controls="account-settings-popover"
                              aria-expanded={profileOpen}
                              aria-haspopup="dialog"
                              aria-label={t('account')}
                              className="grid size-9 place-items-center overflow-hidden rounded-full bg-gradient-to-br from-primary via-violet-600 to-fuchsia-700 text-xs font-bold text-white ring-2 ring-transparent transition hover:ring-primary/30 focus-visible:outline-2 focus-visible:outline-primary"
                              onClick={() => setProfileOpen(value => !value)}
                              ref={accountTriggerRef}
                              type="button"
                            >
                              {session.user.avatarUrl ? (
                                <AppImage
                                  alt=""
                                  className="size-full object-cover"
                                  height={36}
                                  src={session.user.avatarUrl}
                                  width={36}
                                  wrapperClassName="size-full"
                                />
                              ) : (
                                initials
                              )}
                            </button>
                            {profileOpen && (
                              <AccountSettingsPopover
                                onClose={() => setProfileOpen(false)}
                                onSwitchWorkspace={() =>
                                  setWorkspaceSwitcherOpen(true)
                                }
                                open
                                suspended={workspaceSwitcherOpen}
                                triggerRef={accountTriggerRef}
                                workspaceTriggerRef={
                                  workspaceSwitcherTriggerRef
                                }
                              />
                            )}
                          </div>
                        </>
                      ) : (
                        <>
                          <Link
                            className="hidden min-h-9 items-center px-3 text-sm font-medium text-muted hover:text-foreground sm:inline-flex"
                            to={`/login?returnTo=${encodeURIComponent(location.pathname)}`}
                          >
                            {tc('signIn')}
                          </Link>
                          <Link
                            className="inline-flex min-h-9 items-center rounded-full bg-pill px-4 text-sm font-semibold text-pill-text transition hover:bg-primary hover:text-white focus-visible:outline-2 focus-visible:outline-primary"
                            to={`/login?returnTo=${encodeURIComponent(location.pathname)}`}
                          >
                            {tc('startCreating')}
                          </Link>
                        </>
                      )}
                    </div>
                    {workspaceSwitcherOpen ? (
                      <WorkspaceSwitcherModal
                        onClose={() => setWorkspaceSwitcherOpen(false)}
                        triggerRef={workspaceSwitcherTriggerRef}
                      />
                    ) : null}
                  </div>
                </header>

                <main
                  className={cn(
                    'flex min-h-0 flex-1 flex-col',
                    isWorkspaceRoute
                      ? 'overflow-hidden'
                      : 'overflow-y-auto overscroll-contain'
                  )}
                  id="main-content"
                >
                  <div
                    key={outletKey}
                    className={cn(
                      isWorkspaceRoute
                        ? 'flex min-h-0 flex-1 flex-col overflow-hidden p-0'
                        : 'px-4 pt-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] sm:px-6 sm:py-8 lg:px-10'
                    )}
                  >
                    <Outlet />
                  </div>
                </main>
              </div>
            </div>

            {mobileOpen && (
              <>
                <button
                  aria-label={ts('navigation.close')}
                  className="fixed inset-0 z-50 bg-overlay backdrop-blur-sm focus-visible:outline-2 focus-visible:outline-primary lg:hidden"
                  onClick={() => setMobileOpen(false)}
                  type="button"
                />
                <aside className="fixed inset-y-0 left-0 z-[60] flex w-[min(18rem,calc(100vw-2rem))] flex-col overflow-hidden overscroll-contain border-r border-border bg-elevated px-4 pt-[max(1rem,env(safe-area-inset-top))] pb-[max(1rem,env(safe-area-inset-bottom))] lg:hidden">
                  <div className="mb-6 flex shrink-0 items-center justify-between">
                    <BrandLogo />
                    <button
                      aria-label={ts('navigation.close')}
                      className="grid size-10 place-items-center rounded-full hover:bg-hover focus-visible:outline-2 focus-visible:outline-primary"
                      onClick={() => setMobileOpen(false)}
                      type="button"
                    >
                      <Icon name="close" />
                    </button>
                  </div>
                  <nav
                    aria-label={ts('navigation.mobile')}
                    className="grid shrink-0 gap-1"
                  >
                    {studioNavigation.map(item => (
                      <NavLink
                        className={({ isActive }) =>
                          cn(
                            'flex min-h-11 items-center gap-3 rounded-xl px-3 text-sm font-medium',
                            isActive
                              ? 'bg-nav-active text-nav-active-text'
                              : 'text-muted hover:bg-hover hover:text-foreground'
                          )
                        }
                        end={item.slug === ''}
                        key={`${item.slug || 'home'}-mobile`}
                        onClick={() => setMobileOpen(false)}
                        reloadDocument={item.reloadDocument}
                        to={navPath(item.slug)}
                      >
                        <Icon className="size-[18px]" name={item.icon} />
                        {ts(item.translationKey)}
                      </NavLink>
                    ))}
                  </nav>
                  {isAgentRoute && authenticated && activeWorkspaceId ? (
                    <div className="mt-4 flex min-h-0 flex-1 flex-col">
                      <AgentChatHistory
                        onNavigate={() => setMobileOpen(false)}
                        variant="mobile"
                        workspaceId={activeWorkspaceId}
                      />
                    </div>
                  ) : null}
                </aside>
              </>
            )}
          </div>
        </AgentSessionHeaderProvider>
      </ImageWatermarkProvider>
    </BillingUpgradeProvider>
  );
};
